/** @format */
import {
  Chain,
  Environment,
  ICartItem,
  ModernPromotionResponse,
  ProductPromotionCollection,
  ProductPromotionResponse,
  ProductVO,
  PromotionResponse,
} from '@shared/data-access/interfaces';

import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { HttpClient, HttpHeaders, HttpParams, HttpUrlEncodingCodec } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, catchError, retry, throwError } from 'rxjs';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { isPlatformBrowser } from '@angular/common';
import { CookieService, HttpService } from '@shared/data-access/services';

@Injectable({
  providedIn: 'root',
})
export class DiscountEligibilityDao {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(Environment);
  private readonly cookieService = inject(CookieService);
  private readonly httpService = inject(HttpService);
  private readonly chain = inject(Chain);
  private readonly platformId = inject(PLATFORM_ID);

  // Default number of HTTP retries
  private readonly DEFAULT_HTTP_RETRIES = 2;

  // Default scaling duration in milliseconds for HTTP Retries
  private readonly DEFAULT_HTTP_RETRY_DURATION = 1000;

  // Some methods below are duplicated from the DAOs already in use. When other teams are ready to shift to using this one, they can clear out the old references from their components at their leisure.

  // FOR MODERN
  // From modern-promotion.dao.ts
  public getLongDescription(discountId: string): Observable<PromotionResponse> {
    const MODERN_PROMOTION_HTTP_HEADERS = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
      [CustomHttpHeaders.X_TIMEOUT]: DCSG_CONSTANTS.timeouts.xshort,
      [CustomHttpHeaders.X_CENTRASITE_KEY]: this.environment.apiTokens.esb.api2,
      Env: this.environment.pricing.esbEnvironmentHeader,
    });

    const httpOptions = {
      headers: MODERN_PROMOTION_HTTP_HEADERS,
    };

    const url = `/longdescription/${discountId}`;

    return this.http.get<PromotionResponse>(url, httpOptions);
  }

  /**
   *
   * @param productList
   * @returns
   * list of allowed values: "dsg-ecomm", "gg-ecomm", "fs-ecomm", "pl-ecomm", "calia-ecomm", "vrst-ecomm", "ggg-ecomm"
   */
  getModernProductPromoList(
    productList: ProductVO[] | ICartItem[],
  ): Observable<ProductPromotionResponse[]> {
    const ecodes = [
      ...new Set(
        productList.map((item) => {
          return (item as ICartItem).ecode || (item as ProductVO).parentPartnumber;
        }),
      ),
    ];

    const MODERN_PROMOTION_HTTP_HEADERS = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
      [CustomHttpHeaders.X_TIMEOUT]: DCSG_CONSTANTS.timeouts.xshort,
      [CustomHttpHeaders.X_CENTRASITE_KEY]: this.environment.apiTokens.esb.api2,
      [CustomHttpHeaders.X_PROXY_REQUEST]: 'true',
      [CustomHttpHeaders.SWIMLANE]: this.cookieService
        .getSwimlaneValue(this.httpService.getHostName())
        .toString(),
    });

    if (!isPlatformBrowser(this.platformId)) {
      MODERN_PROMOTION_HTTP_HEADERS.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${this.environment.api.esb.gatewayUrlProxy.timeout}`,
      );
    }

    const httpOptions = {
      headers: MODERN_PROMOTION_HTTP_HEADERS,
    };

    return this.http
      .post<ProductPromotionResponse[]>(
        `/promomessages/${this.chain.chainIdentifierAbbr === 'g3' ? 'ggg' : this.chain.chainIdentifierAbbr}-ecomm/`,
        {
          ecodes: ecodes,
          onlineChannel: 'WEB',
        },
        httpOptions,
      )
      .pipe(
        retry({
          count: this.DEFAULT_HTTP_RETRIES,
          delay: this.DEFAULT_HTTP_RETRY_DURATION,
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  getPromoMessage(ecode: string, sku?: string | string[]): Observable<ModernPromotionResponse> {
    const MODERN_PROMOTION_HTTP_HEADERS = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
      'Content-Type': MediaType.APPLICATION_JSON,
      'Access-Control-Allow-Origin': '*',
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
      [CustomHttpHeaders.X_TIMEOUT]: DCSG_CONSTANTS.timeouts.xshort,
      [CustomHttpHeaders.X_CENTRASITE_KEY]: this.environment.apiTokens.esb.api2,
      [CustomHttpHeaders.X_PROXY_REQUEST]: 'true',
      [CustomHttpHeaders.SWIMLANE]: this.cookieService
        .getSwimlaneValue(this.httpService.getHostName())
        .toString(),
    });

    if (!isPlatformBrowser(this.platformId)) {
      MODERN_PROMOTION_HTTP_HEADERS.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${this.environment.api.esb.gatewayUrlProxy.timeout}`,
      );
    }

    const httpOptions = {
      headers: MODERN_PROMOTION_HTTP_HEADERS,
    };

    return this.http
      .post<ModernPromotionResponse>(
        `/promomessages/${this.chain.chainIdentifierAbbr === 'g3' ? 'ggg' : this.chain.chainIdentifierAbbr}-ecomm/${ecode}`,
        {
          onlineChannel: 'WEB',
          sku: sku,
        },
        httpOptions,
      )
      .pipe(
        retry({
          count: this.DEFAULT_HTTP_RETRIES,
          delay: this.DEFAULT_HTTP_RETRY_DURATION,
        }),
        catchError((error) => {
          return throwError(() => error);
        }),
      );
  }

  // FOR LEGACY
  // From shared-product-catalog.service.ts
  /**
   * @description
   * Get availability information for each ecode in the product list
   * @param productList List of products from product catalog data
   */
  getLegacyProductPromoList(
    productList: ProductVO[] | ICartItem[],
  ): Observable<ProductPromotionCollection> {
    const catEntryIds = [
      ...new Set(
        productList.map((item) => {
          return (item as ProductVO).parentCatentryId || (item as ProductVO).catentryId;
        }),
      ),
    ];

    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');
    httpHeaders = httpHeaders.set(
      CustomHttpHeaders.X_CENTRASITE_KEY,
      this.environment.api.esb.promotions.apiKey,
    );
    httpHeaders = httpHeaders.set(
      CustomHttpHeaders.ENVIRONMENT,
      this.environment.api.esb.promotions.env,
    );

    if (!isPlatformBrowser(this.platformId)) {
      httpHeaders = httpHeaders.set(
        CustomHttpHeaders.X_TIMEOUT,
        `${this.environment.api.esb.gatewayUrlProxy.timeout}`,
      );
    }

    const httpParams = new HttpParams({ encoder: new CustomEncoder() })
      .set('catEntryIds', `[${catEntryIds}]`)
      .set('previewTime', this.environment.api.esb.promotions.previewTime)
      .set('channelId', this.environment.api.esb.promotions.channelId);

    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
    };

    return this.http.get(`/discounts/${this.chain.storeId}`, httpOptions).pipe(
      retry({
        count: this.DEFAULT_HTTP_RETRIES,
        delay: this.DEFAULT_HTTP_RETRY_DURATION,
      }),
      catchError((error) => {
        return throwError(() => error);
      }),
    );
  }

  // From checkout.dao.ts
  /**
   * @returns {Observable<string>}
   *
   * @description
   * Gets promotions for the current items in the cart for the checkout object
   *
   * @param checkoutKey
   * @param promotionId
   */
  public getPromotionDescription(checkoutKey: string, promotionId: number): Observable<string> {
    const httpHeaders = new HttpHeaders({
      Accept: 'text/html;charset=utf-8',
      'Content-Type': 'text/html;charset=utf-8',
      'Access-Control-Allow-Origin': '*',
      'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
      Pragma: 'no-cache',
      Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
    });

    const httpOptions = {
      headers: httpHeaders,
      responseType: 'text' as const,
      withCredentials: true,
    };

    httpOptions.headers = httpOptions.headers.set(
      CustomHttpHeaders.X_TIMEOUT,
      `${DCSG_CONSTANTS.timeouts.xshort}`,
    );

    return this.http.get(
      `/api/v1/checkouts/${checkoutKey}/promotion/cart/promotions/${promotionId}/description`,
      httpOptions,
    );
  }
}

export class CustomEncoder extends HttpUrlEncodingCodec {
  encodeKey(k: string): string {
    return encodeURIComponent(k);
  }

  encodeValue(v: string): string {
    return encodeURIComponent(v);
  }

  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }
}
